import React, { useState } from 'react';
import { useCourseModuleList } from '@/hooks/queries/course-module/useList';
import { useCourseModuleCreate, useCourseModuleUpdate, useCourseModuleDelete, useCourseModuleReorder } from '@/hooks/mutations/course-module';
import { Module, ModuleCreateInput } from '@/types/modules';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Layers, ArrowUpDown, Lock, Unlock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CourseSelect } from '@/components/CourseSelect';
import { CourseCell } from '@/components/CourseCell';
import { Pagination } from '@/components/Pagination';

const CourseModulesPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [courseIdFilter, setCourseIdFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);

  const [formData, setFormData] = useState<ModuleCreateInput>({
    courseId: '',
    isLocked: false,
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: modulesData, isLoading, refetch } = useCourseModuleList({
    courseId: courseIdFilter || undefined,
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createModule, isPending: isCreating } = useCourseModuleCreate();
  const { mutate: updateModule, isPending: isUpdating } = useCourseModuleUpdate();
  const { mutate: deleteModule } = useCourseModuleDelete();

  const handleOpenCreate = () => {
    setSelectedModule(null);
    setFormData({
      courseId: courseIdFilter || '',
      isLocked: false,
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (mod: Module) => {
    setSelectedModule(mod);
    setFormData({
      courseId: mod.courseId,
      isLocked: mod.isLocked,
      translations: [
        { locale: 'uz', title: mod.title || '', description: mod.description || '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedModule) {
      updateModule(
        { id: selectedModule.id, data: { isLocked: formData.isLocked, translations: formData.translations } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Modul yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Modul yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createModule(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi modul yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Modul yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedModule) return;
    deleteModule(selectedModule.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Modul o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedModule(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Modul o'chirilmadi", variant: 'destructive' }),
    });
  };

  const modulesList: Module[] = modulesData?.data?.data || modulesData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Modullari</h1>
          <p className="text-sm text-slate-500 mt-1">Kurslar ichidagi modullar, darslar va testlar tuzilishi</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi modul
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <CourseSelect
              value={courseIdFilter}
              onChange={(id) => { setCourseIdFilter(id); setPage(1); }}
              allowEmpty={true}
              emptyLabel="Barcha kurslar bo'yicha filter"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : modulesList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Layers className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha modullar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Tartib</th>
                  <th className="px-4 py-3.5">Modul Nomi</th>
                  <th className="px-4 py-3.5">Kurs</th>
                  <th className="px-4 py-3.5">Darslar / Testlar</th>
                  <th className="px-4 py-3.5">Holati</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {modulesList.map((mod) => (
                  <tr key={mod.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      <span className="size-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-bold">
                        {mod.orderIndex}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{mod.title || '—'}</td>
                    <td className="px-4 py-3">
                      <CourseCell courseId={mod.courseId} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <span className="text-primary font-medium">{mod.lessonCount || 0} dars</span> / <span>{mod.testCount || 0} test</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={mod.isLocked ? 'secondary' : 'default'} className={!mod.isLocked ? 'bg-emerald-500 text-white' : ''}>
                        {mod.isLocked ? <Lock className="size-3 mr-1" /> : <Unlock className="size-3 mr-1" />}
                        {mod.isLocked ? 'Qulflangan' : 'Ochiq'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(mod)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedModule(mod);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={modulesData?.meta?.pagination ?? modulesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedModule ? 'Modulni tahrirlash' : 'Yangi modul yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {!selectedModule && (
              <CourseSelect
                label="Kurs"
                value={formData.courseId}
                onChange={(id) => setFormData({ ...formData, courseId: id })}
                required={true}
              />
            )}

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Qulflangan holat (isLocked)</Label>
                <p className="text-xs text-slate-500">Talaba oldingi modulni tugatmaguncha bu modul yopiq bo'ladi</p>
              </div>
              <Switch
                checked={formData.isLocked}
                onCheckedChange={(checked) => setFormData({ ...formData, isLocked: checked })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Modul nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="JavaScript Asoslari"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="O'zgaruvchilar, funksiyalar va tsikllar..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Modulni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu modul va uning ichidagi darslar o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CourseModulesPage;
